// The one shader pipeline every subset module locks and exposes ids of (SHARED §Pipeline contract). Node-safe data.
import { VOCAB } from '../vocab.js'
import camera from './camera.glsl.js'
import displacement from './displacement.glsl.js'
import lattice from './lattice.glsl.js'
import light from './light.glsl.js'
import main from './main.glsl.js'
import palette from './palette.glsl.js'
import profile from './profile.glsl.js'
import relief from './relief.glsl.js'
import surface from './surface.glsl.js'

const STAGES = { camera, surface, lattice, displacement, relief, profile, light, palette, main }

const camel = id =>
	'u' +
	id
		.split('_')
		.map(w => w[0].toUpperCase() + w.slice(1))
		.join('')

// `uColor` is Pixi's tint, so the palette colours bind A/B; the ground is a colour with alpha.
const COLORS = {
	color: { uniform: 'uColorA', glsl: 'vec3' },
	color_b: { uniform: 'uColorB', glsl: 'vec3' },
	ground: { uniform: 'uGround', glsl: 'vec4', alpha: true },
}

// Per id, what today's modules declared per param, once: the uniform it binds, its GLSL type when not float, and its clock flags.
const OWN = {
	speed: {
		integrate: true,
		// Rows' pitch is spacing + gap · width, floored at light.glsl.js PITCH_MIN.
		per: { plane: ['uWidth', 'uSpacing'], rows: ['uSpacing'], rowsGap: ['uGap', 'uWidth'], rowsMin: 1 },
		base: 33.75,
		cycle: 1,
		carry: true,
		coast: true,
		// The camera's place in its tunnel piece: TN_PIECE_SU su of travel a piece (surface.glsl.js).
		depth: 200,
	},
	// At 1, one swing per 4 lengths the bend lets you see (4 / √(0.08 × bend) radii, 100 su each); bend_roll one turn per 10 pieces.
	wander: {
		integrate: true,
		along: 'speed',
		base: Math.sqrt(0.08) / 1600,
		times: { uniform: 'uBend', power: 0.5 },
		cycle: 1,
		settle: 1,
		live: true,
	},
	// Both settle: at rate 0 the phase coasts to a whole turn, so a still path curves one way in bend_angle's plane.
	bend_roll: { integrate: true, along: 'speed', base: 1 / 2000, cycle: 1, settle: 1, live: true },
	// The blob field's own motion, apart from the rings' travel (speed); its rate gathers the blobs to the centre near 0.
	temperature: { integrate: true, base: 0.57, cycle: 32, live: true },
	// The bands' wave phase, in waves; rows' waves move by the sea (sea.js).
	wave_speed: { integrate: true, wall: true, per: ['uWaveLength'], cycle: 1 },
	wobble_rate: { integrate: true, base: 0.3, cycle: 20 },
	ring_speed: { integrate: true, wall: true, cycle: 1, carry: true },
	wash: { integrate: true, settle: 1, cycle: 1, carry: true },
	pan: { glsl: 'vec2' },
	// A path tweens it 0 ramp → 1 mirror; the palette blends the two.
	repeat: { kind: 'select', enum: [0, 1], seam: true },
	stretch: { seam: true },
	angle: { wrap: true },
	roll: { wrap: true },
	wave_direction: { wrap: true },
	bend_angle: { wrap: true },
	light_angle: { wrap: true },
}

export const PIPELINE_IDS = Object.values(STAGES).flatMap(s => s.ids)

export const PARAMS = Object.fromEntries(
	PIPELINE_IDS.map(id => [id, COLORS[id] ? { ...COLORS[id], kind: 'color' } : { uniform: camel(id), ...OWN[id] }])
)

export const STAGE_OF = Object.fromEntries(
	Object.entries(STAGES).flatMap(([stage, { ids }]) => ids.map(id => [id, stage]))
)

// Flat stripes (pipeline.md §The world); perspective has no effect at tilt 90 with elevation 0.
const FLAT = {
	tilt: 90,
	perspective: 1,
	walls: 0,
	start: 0,
	// The plane; ribbons locks 1, its rows.
	rows: 0,
	// Rows' pitch as the plane's: spacing + width; ribbons locks 0.
	gap: 1,
	wave_height: 0,
	wave_b_height: 0,
	wave_c_height: 0,
	parallax: 1,
	fill: 0,
	blobs: 0,
	wobble_view: 0,
	relief: 0,
	cross: 0,
	light: 0,
	fog: 0,
	// All: lines cover the frame.
	count: VOCAB.count.maximum,
	enclose: 0,
	// Transparent: lines over whatever lies beneath.
	ground: '#00000000',
	tunnel: 0,
	twist: 0,
	// The tube's arm sheared into rings at 1; spiral locks 0, so the shear is its path to every subset.
	unwind: 1,
	rings: 0,
	ring_speed: 0,
	// Still unless a subset exposes them: a locked 0 is a constant, not a driven clock.
	wave_speed: 0,
	temperature: 0,
	wobble_rate: 0,
	// Blobs drawn out into the bands at 1, where they paint nothing; plasma locks 0.
	stretch: 1,
}

// Where the registry has no initial yet: today's stripes initials, else the inert value.
const FALLBACK = {
	speed: 1,
	zoom: 1,
	roll: 0,
	elevation: 0,
	pan: [0, 0],
	fit: 1,
	rounding: 0,
	fold: 0,
	radius: 4,
	bend: 0,
	bend_angle: 0,
	wander: 0,
	bend_roll: 0,
	light_angle: 0,
	angle: 0,
	width: 8,
	spacing: 0,
	grow: 0,
	wave_length: 200,
	shape: 0,
	wave_b_length: 600,
	wave_b_speed: 1.75,
	wave_c_length: 80,
	wave_c_speed: 0.62,
	wave_direction: 0,
	hill_rows: 0.1,
	blob_size: 120,
	blob_soft: 40,
	// Voxel steps as accepted on local/test-pages/voxel.html.
	cube_size: 50,
	cube_levels: 3,
	cube_fill: 0.5,
	cube_split: 0.5,
	cube_split_fill: 0.6,
	cube_corner: 0.5,
	cube_cluster: 0.5,
	cube_cluster_size: 6,
	cube_seed: 1,
	color: '#33d6ff',
	color_b: '#2a1a7a',
	spread: 2,
	repeat: 0,
	wash: 0,
	spread_from: 0,
	spread_to: 0,
}

export const DEFAULT_VECTOR = Object.fromEntries(
	PIPELINE_IDS.map(id => [id, FLAT[id] ?? VOCAB[id]?.initial ?? FALLBACK[id]])
)

export const PIPELINE = {
	ids: PIPELINE_IDS,
	stages: Object.entries(STAGES).map(([name, { ids }]) => ({ name, ids })),
	source:
		main.head +
		Object.values(STAGES)
			.map(s => s.glsl)
			.join('\n'),
	trail: null,
}
